import React, { createContext, useContext, useState } from 'react';
import {
  ScheduleIndexResponse,
  SchedulesTransformedShape,
} from '@/dashboard/services/dashboardApi';
import { transformSchedulesResponse } from '@/dashboard/utils/schoolYearSelector.utils';

type DashboardBatchSubmitActionsType = {
  submitStart: () => void;
  submitEnd: () => void;
};

interface DashboardContextType {
  batchSubmitActions: DashboardBatchSubmitActionsType;
  setBatchSubmitActions: React.Dispatch<React.SetStateAction<DashboardBatchSubmitActionsType>>;
  schedules: SchedulesTransformedShape | undefined;
  appState: {
    submit: { isSubmitting: boolean; error: string | null; progress: number | null };
  };
  setSubmitError: (error: string | null) => void;
  setSubmitProgress: (progress: number | null) => void;
  isPolling: boolean;
  setIsPolling: (isPolling: boolean) => void;
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

function DashboardContextProvider({
  children,
  schedules,
}: {
  children: React.ReactNode;
  schedules: ScheduleIndexResponse | undefined;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitProgress, setSubmitProgress] = useState<number | null>(null); // Added state for submit progress
  const [isPolling, setIsPolling] = useState(false);
  const [batchSubmitActions, setBatchSubmitActions] = useState({
    submitStart: () => {
      setIsSubmitting(true);
      setSubmitProgress(0); // Initialize progress on start
    },
    submitEnd: () => {
      setIsSubmitting(false);
      setSubmitProgress(null); // Reset progress on end
    },
  });
  const appState = { submit: { isSubmitting, error: submitError, progress: submitProgress } };

  const transformed = schedules ? transformSchedulesResponse(schedules) : undefined;

  return (
    <DashboardContext.Provider
      value={{
        batchSubmitActions,
        setBatchSubmitActions,
        schedules: transformed,
        appState,
        setSubmitError,
        setSubmitProgress,
        isPolling,
        setIsPolling,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
}

function useDashboardContext() {
  const context = useContext(DashboardContext);

  if (context === undefined) {
    throw new Error('Incorrect use of Context');
  }

  return context;
}

export { DashboardContextProvider, useDashboardContext };
